// Readiness checks for guided tour targets. A step is only revealed once its
// [data-tour] elements are in the DOM, visible, done animating and holding a
// steady position, so the spotlight never lands on a half-rendered page.
//
// Every wait takes an AbortSignal (cancelled when the step changes) and a
// timeout. Timeouts resolve rather than reject so the caller can fall back
// gracefully; aborts reject with an AbortError.

export const RECT_TOLERANCE_PX = 0.5
export const STABLE_FRAMES = 6

const createAbortError = () => {
  const error = new Error('Tour step changed')
  error.name = 'AbortError'
  return error
}

export const isAbortError = (error) => error?.name === 'AbortError'

// Sub-pixel jitter (fractional zoom, transforms) shouldn't count as movement
export const rectsMatch = (a, b, tolerance = RECT_TOLERANCE_PX) => {
  if (!a || !b) return false
  return ['top', 'left', 'width', 'height'].every(key => Math.abs(a[key] - b[key]) <= tolerance)
}

const rectListsMatch = (a, b) => a.length === b.length && a.every((rect, index) => rectsMatch(rect, b[index]))

export const isElementVisible = (el) => {
  if (!el || !el.isConnected) return false
  const rect = el.getBoundingClientRect()
  if (rect.width === 0 || rect.height === 0) return false
  const style = window.getComputedStyle(el)
  return style.visibility !== 'hidden' && style.display !== 'none'
}

// First visible element carrying the name (hooks on list items repeat)
export const findVisibleTarget = (name) => {
  return [...document.querySelectorAll(`[data-tour="${name}"]`)].find(isElementVisible) || null
}

export const findAllVisibleTargets = (name) => {
  return [...document.querySelectorAll(`[data-tour="${name}"]`)].filter(isElementVisible)
}

// Settles `promise`, or resolves `fallback` after `timeoutMs`, or rejects on abort
const raceWithTimeout = (promise, { signal, timeoutMs, fallback = null }) => new Promise((resolve, reject) => {
  if (signal?.aborted) return reject(createAbortError())
  const onAbort = () => finish(reject, createAbortError())
  const timer = setTimeout(() => finish(resolve, fallback), timeoutMs)
  if (signal) signal.addEventListener('abort', onAbort)
  function finish (settle, value) {
    clearTimeout(timer)
    if (signal) signal.removeEventListener('abort', onAbort)
    settle(value)
  }
  promise.then(value => finish(resolve, value), error => finish(reject, error))
})

// Resolves with the first truthy result of `check()`, re-checking whenever the
// DOM changes (at most once per frame). Resolves null on timeout.
export const waitForCondition = (check, { signal, timeoutMs }) => {
  const initial = check()
  if (initial) return Promise.resolve(initial)

  let observer = null
  let frame = null
  const found = new Promise(resolve => {
    observer = new MutationObserver(() => {
      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = null
        const result = check()
        if (result) resolve(result)
      })
    })
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'data-tour', 'data-tour-ready']
    })
  })

  const cleanup = () => {
    observer.disconnect()
    if (frame) cancelAnimationFrame(frame)
  }
  return raceWithTimeout(found, { signal, timeoutMs }).then(
    result => { cleanup(); return result },
    error => { cleanup(); throw error }
  )
}

// Finite CSS animations / transitions running on the elements or any ancestor
// (page fade-ins, dropdowns opening). Infinite ones (spinners) are ignored.
const runningAnimationsAffecting = (els) => {
  if (typeof document.getAnimations !== 'function') return []
  return document.getAnimations().filter(animation => {
    const target = animation.effect?.target
    if (!target || animation.playState !== 'running') return false
    if (animation.effect.getTiming().iterations === Infinity) return false
    return els.some(el => target === el || target.contains(el))
  })
}

export const waitForAnimations = (els, { signal, timeoutMs }) => {
  const running = runningAnimationsAffecting(els)
  if (!running.length) return Promise.resolve()
  const finished = Promise.all(running.map(animation => animation.finished.catch(() => {})))
  return raceWithTimeout(finished, { signal, timeoutMs })
}

// Resolves once the rects from `readRects()` hold still for `stableFrames`
// consecutive frames (smooth scrolls, late layout shifts). Resolves anyway on timeout.
export const waitForStableLayout = (readRects, {
  signal,
  timeoutMs,
  stableFrames = STABLE_FRAMES,
  scheduleFrame = (callback) => requestAnimationFrame(callback),
  cancelFrame = (id) => cancelAnimationFrame(id)
}) => {
  let frame = null
  const stable = new Promise(resolve => {
    let previous = readRects()
    let stillFrames = 0
    const tick = () => {
      const current = readRects()
      stillFrames = rectListsMatch(current, previous) ? stillFrames + 1 : 0
      previous = current
      if (stillFrames >= stableFrames) return resolve()
      frame = scheduleFrame(tick)
    }
    frame = scheduleFrame(tick)
  })
  const cleanup = () => { if (frame) cancelFrame(frame) }
  return raceWithTimeout(stable, { signal, timeoutMs }).then(
    result => { cleanup(); return result },
    error => { cleanup(); throw error }
  )
}
